import React, { useState, useEffect, useCallback } from "react";
import AddPharmacyItem from "./AddPharmacyItems";
import { getPharmacyItems, getPharmacyItemById, updatePharmacyItem } from "../../apis/pharmacyItems";
import { Button, Dropdown, Input, Select, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { MedicineBoxOutlined, SearchOutlined } from "@ant-design/icons";
import { BeakerIcon } from "@heroicons/react/24/solid";
import CommonPagination from "../../components/CommanPagination";

const { Option } = Select;

const fmt = (n: number) => Number(n ?? 0).toLocaleString();

const stockColor = (qty: number) =>
  qty === 0 ? "red" : qty <= 10 ? "orange" : "green";

const expiryTag = (label: string, count: number, color: string) =>
  count > 0 ? (
    <Tag color={color} className="text-xs">
      {count} {label}
    </Tag>
  ) : null;

const BatchTable: React.FC<{ batches: any[] }> = ({ batches }) => {
  const cols: ColumnsType<any> = [
    {
      title: "Batch No",
      dataIndex: "batchNumber",
      key: "batchNumber",
      render: (v: string) => <span className="font-mono text-xs">{v || "—"}</span>,
    },
    {
      title: "Expiry",
      dataIndex: "expiryDate",
      key: "expiryDate",
      render: (v: string, row: any) => {
        if (!v) return <span className="text-gray-400">—</span>;
        const label = new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        if (row.isExpired) return <Tag color="red">{label} · Expired</Tag>;
        if (row.isExpiringSoon) return <Tag color="orange">{label} · Expiring Soon</Tag>;
        return <span className="text-gray-700 text-xs">{label}</span>;
      },
    },
    {
      title: "Qty",
      dataIndex: "availableQuantity",
      key: "availableQuantity",
      align: "right" as const,
      render: (v: number, row: any) => (
        <span
          className="font-semibold tabular-nums"
          style={{ color: stockColor(v ?? 0) }}
        >
          {fmt(v ?? 0)}
        </span>
      ),
    },
    {
      title: "MRP (₹)",
      key: "mrp",
      align: "right" as const,
      render: (_: any, row: any) => {
        const mrp = parseFloat(row.sellingPrice ?? row.selling_price ?? "0");
        return <span className="tabular-nums text-xs">₹{mrp.toFixed(2)}</span>;
      },
    },
    {
      title: "Purchase (₹)",
      key: "purchase",
      align: "right" as const,
      render: (_: any, row: any) => {
        const p = parseFloat(row.purchasePrice ?? row.purchase_price ?? "0");
        return <span className="tabular-nums text-xs text-gray-500">₹{p.toFixed(2)}</span>;
      },
    },
    {
      title: "Tax %",
      dataIndex: "taxPercentage",
      key: "tax",
      align: "right" as const,
      render: (v: any) => (v != null ? `${v}%` : "—"),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (v: boolean) => (
        <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>
      ),
    },
  ];

  return (
    <Table
      rowKey="id"
      size="small"
      pagination={false}
      dataSource={batches}
      columns={cols}
      className="bg-blue-50 rounded"
      rowClassName={(row: any) =>
        row.isExpired
          ? "bg-red-50"
          : row.isExpiringSoon
          ? "bg-orange-50"
          : ""
      }
    />
  );
};

const PharmacyItemsList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pharmacyItems, setPharmacyItems] = useState<any[]>([]);
  const [editableItem, setEditableItem] = useState<any>(null);
  const [meta, setMeta] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({
    name: "",
    company: "",
    form: "",
    status: "",
    expiryStatus: "",
    stockStatus: "",
  });

  const fetchWithFilters = useCallback(
    (overrides: any = {}) => {
      const params: any = {
        page: overrides.page ?? page,
        per_page: overrides.per_page ?? pageSize,
      };
      if (filters.name.trim()) params.name = filters.name.trim();
      if (filters.company.trim()) params.company = filters.company.trim();
      if (filters.form.trim()) params.form = filters.form.trim();
      if (filters.status === "active") params.is_active = true;
      if (filters.status === "inactive") params.is_active = false;
      if (filters.expiryStatus) params.expiry_status = filters.expiryStatus;
      if (filters.stockStatus) params.stock_status = filters.stockStatus;

      setLoading(true);
      getPharmacyItems(params)
        .then((res: any) => {
          setPharmacyItems(res.data || []);
          setMeta(res.meta);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    },
    [filters, page, pageSize],
  );

  useEffect(() => {
    const t = setTimeout(() => fetchWithFilters({ page: 1 }), 300);
    return () => clearTimeout(t);
  }, [filters]);

  const setFilter = (key: keyof typeof filters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleEditItem = (item: any) => {
    getPharmacyItemById(item.id)
      .then((res: any) => {
        setEditableItem(res);
        setIsModalOpen(true);
      })
      .catch(console.error);
  };

  const handleToggleConsumable = (item: any) => {
    updatePharmacyItem(item.id, { is_consumable: !item.isConsumable }).then(() =>
      setPharmacyItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, isConsumable: !i.isConsumable } : i)),
      ),
    );
  };

  const handleToggleActive = (item: any) => {
    updatePharmacyItem(item.id, { is_active: !item.isActive }).then(() =>
      setPharmacyItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, isActive: !i.isActive } : i)),
      ),
    );
  };

  const onPaginationChange = (p: number, ps?: number) => {
    setPage(p);
    setPageSize(ps ?? pageSize);
    fetchWithFilters({ page: p, per_page: ps ?? pageSize });
  };

  const columns: ColumnsType<any> = [
    {
      title: "Item",
      key: "name",
      width: 240,
      render: (_: any, item: any) => (
        <div className="flex items-center gap-2">
          <Tooltip title={item.isConsumable ? "Consumable" : "Non-Consumable"}>
            {item.isConsumable ? (
              <BeakerIcon
                className="h-5 w-5 text-gray-500 flex-shrink-0 cursor-pointer"
                onClick={() => handleToggleConsumable(item)}
              />
            ) : (
              <MedicineBoxOutlined
                style={{ fontSize: 16, color: "#6b7280", cursor: "pointer" }}
                onClick={() => handleToggleConsumable(item)}
              />
            )}
          </Tooltip>
          <div>
            <div className="font-medium text-gray-900 text-sm">{item.name}</div>
            {item.sku && <div className="text-xs text-gray-400">{item.sku}</div>}
          </div>
        </div>
      ),
    },
    {
      title: "Company",
      key: "company",
      width: 140,
      render: (_: any, item: any) => (
        <span className="text-sm text-gray-700">{item.company?.name ?? "—"}</span>
      ),
    },
    {
      title: "Form / Strength",
      key: "form",
      width: 150,
      render: (_: any, item: any) => (
        <span className="text-sm text-gray-700">
          {[item.form, item.strength, item.unit].filter(Boolean).join(" · ") || "—"}
        </span>
      ),
    },
    {
      title: "Total Stock",
      key: "stock",
      width: 110,
      align: "right" as const,
      render: (_: any, item: any) => {
        const batches: any[] = item.batches ?? [];
        const activeQty = batches
          .filter((b: any) => !b.isExpired && b.isActive !== false)
          .reduce((s: number, b: any) => s + Number(b.availableQuantity ?? 0), 0);
        return (
          <span
            className="text-base font-bold tabular-nums"
            style={{ color: stockColor(activeQty) }}
          >
            {fmt(activeQty)}
          </span>
        );
      },
    },
    {
      title: "Expiry Status",
      key: "expiry",
      width: 180,
      render: (_: any, item: any) => {
        const batches: any[] = item.batches ?? [];
        const expired = batches.filter((b: any) => b.isExpired && Number(b.availableQuantity) > 0).length;
        const soon = batches.filter((b: any) => b.isExpiringSoon && !b.isExpired && Number(b.availableQuantity) > 0).length;
        const ok = batches.filter((b: any) => !b.isExpired && !b.isExpiringSoon && Number(b.availableQuantity) > 0).length;
        if (batches.length === 0) return <span className="text-gray-400 text-xs">No batches</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {expiryTag("Expired", expired, "red")}
            {expiryTag("Exp. Soon", soon, "orange")}
            {ok > 0 && <Tag color="green" className="text-xs">{ok} Good</Tag>}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 90,
      render: (_: any, item: any) => (
        <Tag
          color={item.isActive ? "green" : "red"}
          onClick={() => handleToggleActive(item)}
          className="cursor-pointer"
        >
          {item.isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_: any, item: any) => (
        <Dropdown
          menu={{
            items: [
              { key: "edit", label: <span onClick={() => handleEditItem(item)}>Edit</span> },
            ],
          }}
          placement="bottomRight"
        >
          <Button size="small">Actions</Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-base font-semibold text-gray-900">Pharmacy Items</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {meta?.total != null ? `${meta.total} items` : ""}
          </p>
        </div>
        <button
          onClick={() => { setEditableItem(null); setIsModalOpen(true); }}
          className="px-3 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover"
        >
          Add Pharmacy Item
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
        <Input
          placeholder="Item name..."
          prefix={<SearchOutlined className="text-gray-400" />}
          value={filters.name}
          onChange={(e) => setFilter("name", e.target.value)}
          allowClear
          size="small"
        />
        <Input
          placeholder="Company..."
          value={filters.company}
          onChange={(e) => setFilter("company", e.target.value)}
          allowClear
          size="small"
        />
        <Input
          placeholder="Form..."
          value={filters.form}
          onChange={(e) => setFilter("form", e.target.value)}
          allowClear
          size="small"
        />
        <Select
          placeholder="Status"
          value={filters.status || undefined}
          onChange={(v) => setFilter("status", v ?? "")}
          allowClear
          size="small"
          style={{ width: "100%" }}
        >
          <Option value="active">Active</Option>
          <Option value="inactive">Inactive</Option>
        </Select>
        <Select
          placeholder="Expiry"
          value={filters.expiryStatus || undefined}
          onChange={(v) => setFilter("expiryStatus", v ?? "")}
          allowClear
          size="small"
          style={{ width: "100%" }}
        >
          <Option value="expired">Has Expired Stock</Option>
          <Option value="expiring_soon">Expiring within 90 days</Option>
        </Select>
        <Select
          placeholder="Stock"
          value={filters.stockStatus || undefined}
          onChange={(v) => setFilter("stockStatus", v ?? "")}
          allowClear
          size="small"
          style={{ width: "100%" }}
        >
          <Option value="out_of_stock">Out of Stock</Option>
          <Option value="low_stock">Low Stock (≤10)</Option>
        </Select>
      </div>

      {/* Table */}
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={pharmacyItems}
        columns={columns}
        pagination={false}
        expandable={{
          rowExpandable: (record) => (record?.batches?.length ?? 0) > 0,
          expandedRowRender: (record) => (
            <div className="py-2 px-4">
              <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                Batches — {record.name}
              </p>
              <BatchTable batches={record.batches ?? []} />
            </div>
          ),
          expandRowByClick: false,
        }}
        locale={{ emptyText: "No pharmacy items found." }}
      />

      <CommonPagination meta={meta} onChange={onPaginationChange} />

      {/* Add / Edit modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[96vh] overflow-y-auto relative">
            <div className="flex items-center justify-between bg-primary text-white px-4 py-3 rounded-t-lg">
              <h2 className="text-lg font-semibold">
                {editableItem ? "Update Pharmacy Item" : "Add Pharmacy Item"}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-white hover:text-gray-200 text-xl font-bold"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <AddPharmacyItem
                closeModal={() => {
                  setIsModalOpen(false);
                  fetchWithFilters({ page, per_page: pageSize });
                  setEditableItem(null);
                }}
                editableItem={editableItem}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacyItemsList;
